import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import Alert from "@mui/material/Alert";
import {
  ivrRequest,
  baseServerUrl,
  login_request,
  checkTokenValid,
  checkLoginFailure,
  TIMEOUT_MESSAGE,
  AXIOS_ERROR,
} from "../../constants/default_request";
import { login, logout } from "../../actions/middleware_actions";
import Token from "../../components/common/RefreshToken";
import OpenForm from "../../forms/IVR/DasOpenBlock/OpenBlockForm";
import { saveIvrTrip } from "../../actions/ivrTripStorage";
import Spinner from "../../components/common/Spinner";
import { BaseAPI } from "../../apis/BaseAPI";

function OpenDasBlockPage({
  auth,
  username,
  apiErrors,
  userLogin,
  userLogout,
  ...props
}) {
  const location = useLocation();
  const { permitNbr } = location.state;
  const navigate = useNavigate();
  const [blockType, setBlockType] = useState("SPAWNING");
  const [portcd, setPortCD] = useState({});
  const [errors, setErrors] = useState({});
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const didLoginFail = checkLoginFailure(apiErrors);
  const [showSpinner, setShowSpinner] = useState(false);

  const values = {
    blockType,
    portcd,
    startDate,
    endDate,
  };

  useEffect(() => {
    didLoginFail ? setOpenDialog(true) : setOpenDialog(false);
  }, [didLoginFail]);

  const handleChange = (input) => (event, reason) => {
    if (input === "block_type") {
      setBlockType(event.target.value);
      event.target.value === "SPAWNING" && startDate
        ? setEndDate(moment(startDate, "MM/DD/YYYY").add(20, "days"))
        : setEndDate(null);
    } else if (input === "port") {
      if (reason) {
        if (reason.hasOwnProperty("id") && reason.hasOwnProperty("label")) {
          setPortCD({ id: reason.id, name: reason.label, state: reason.state });
        }
      } else {
        setPortCD({});
      }
    } else if (input === "start_date") {
      setStartDate(moment(event, "MM/DD/YYYY"));
      if (event && blockType === "SPAWNING") {
        setEndDate(moment(event, "MM/DD/YYYY").add(20, "days"));
      } else if (blockType !== "GILLNET") {
        setEndDate(moment(event, "MM/DD/YYYY"));
      }
    } else if (input === "end_date") {
      setEndDate(moment(event, "MM/DD/YYYY"));
    }
  };

  const formIsValid = () => {
    const formErrors = {};

    if (Object.keys(portcd).length === 0)
      formErrors.port = "Arrival Port is required.";
    if (startDate === null)
      formErrors.startDate = "Block Start Date is required.";
    if (endDate === null) formErrors.endDate = "Block End Date is required.";

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!formIsValid()) return;

    if (!checkTokenValid(auth)) {
      setOpenDialog(true);
      return;
    }
    const actionType = "das_open_block";
    const request_data = ivrRequest(
      auth.client_id,
      auth.token,
      auth.user_app_status,
      permitNbr
    );
    let request_url = `${baseServerUrl}/base_api/evtr/v_1_das_block_open`;
    if (auth.user_app_status === "TEST") {
      request_url = request_url.concat("/test");
    }

    const payload = {
      ...request_data,
      form_name: "DAS OPEN BLOCK",
      form_version: 1,
      vessel_permit_num: permitNbr,
      offloading_port: portcd.id,
      block_start_date: moment(startDate, "MM/DD/YYYY").format("YYMMDD"),
      block_end_date: moment(endDate, "MM/DD/YYYY").format("YYMMDD"),
      block_type: blockType,
    };
    // Setting spinner flag on
    setShowSpinner(true);
    BaseAPI.call(request_url, payload)
      .then((response) => {
        if (response.data.hasOwnProperty("errors")) {
          toast.error(`An error occurred: ${response.data.status}`, {
            autoClose: false,
          });
          console.log("API Error", response.data);
          setErrors({ onSubmitted: response.data.errors });
        } else {
          console.log("API Response", response.data);
          toast.success(`Submission status: ${response.data.status}`);
          navigate(-1);
        }
      })
      .catch((error) => {
        let errorMessage = "";
        if (error.hasOwnProperty("message")) {
          errorMessage = error.message;
        }
        if (error.response) {
          // client received an error response (5xx, 4xx)
          console.log("Error response", error.response, error.message);
        } else if (error.request) {
          // client never received a response, or request never left
          console.log("Bad request", error.request, error.message);
          saveIvrTrip(auth.client_id, actionType, request_url, payload);
        } else if (error.hasOwnProperty("name") && error.name === AXIOS_ERROR) {
          errorMessage = TIMEOUT_MESSAGE;
        } else {
          // anything else
          console.log("Other error", error, error.message);
        }
        toast.error(`An error occurred.  ${errorMessage}`, {
          autoClose: false,
        });
        setErrors({ onSubmitted: errorMessage });
      })
      .finally(() => setShowSpinner(false));
  };

  const handleCancel = () => {
    setShowSpinner(false);
    navigate(-1);
  };

  return (
    <div style={{ width: "100%" }}>
      {openDialog && (
        <Token
          user={username}
          loginRequest={login_request}
          loginUser={userLogin}
          logoutUser={userLogout}
          toggleDialog={setOpenDialog}
        />
      )}
      {errors.hasOwnProperty("onSubmitted") && (
        <Alert severity="error">{errors.onSubmitted}</Alert>
      )}

      <OpenForm
        values={values}
        errors={errors}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
      {showSpinner && <Spinner />}
    </div>
  );
}

const mapStateToProps = (state, ownProps) => {
  const auth = state.auth;
  const username = state.auth.username;
  const apiErrors = state.error;

  return { auth, username, apiErrors };
};

const mapDispatchToProps = {
  userLogin: login,
  userLogout: logout,
};

export default connect(mapStateToProps, mapDispatchToProps)(OpenDasBlockPage);
